import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { styleMap } from "lit/directives/style-map.js";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import { Watch } from "@/decorators/watch";

import styles from "./atlas-image.scss";

enum ImageStatus {
    IDLE = "IDLE",
    LOADING = "LOADING",
    LOADED = "LOADED",
    FALLBACK_LOADING = "FALLBACK_LOADING",
    FALLBACK_LOADED = "FALLBACK_LOADED",
    ERROR = "ERROR"
}

export type ImageProps = AtlasElementProps & {
    "src": string;
    "alt": string;
    "width": number;
    "height": number;
    "border-radius": string;
    "fallback-src": string;
    "fluid": boolean;
};

/**
 * @tag atlas-image
 */
@customElement("atlas-image")
export default class AtlasImage extends AtlasElement {
    static styles = styles;

    /** Propriedade src que define o caminho que definirá aonde está imagem */
    @property({ type: String }) src: string;

    /** Propriedade alt que define o que será apresentado caso a imagem não carregue */
    @property({ type: String }) alt: string;

    /** Propriedade que define a largura da imagem */
    @property({ type: Number }) width: number;

    /** Propriedade que define a altura da imagem */
    @property({ type: Number }) height: number;

    /** Propriedade que define o radius da borda da imagem */
    @property({ type: String, attribute: "border-radius" }) borderRadius: string = "0px";

    /** Indica o caminho da imagem que será apresentada caso ocorra algum erro no carregamento da imagem principal */
    @property({ type: String, attribute: "fallback-src" }) fallbackSrc: string;

    /** Indica que a imagem deve ocupar todo o espaço disponível */
    @property({ type: Boolean, reflect: true }) fluid = false;

    @state() private _status: ImageStatus = ImageStatus.IDLE;

    /** @internal */
    @Watch("src")
    public onSourceChange() {
        this._status = ImageStatus.LOADING;
    }

    /** @internal */
    @Watch("fallbackSrc")
    public onFallbackSourceChange() {
        if (this.isUsingFallback()) {
            this._status = ImageStatus.FALLBACK_LOADING;
        }
    }

    private getSource() {
        return this.isUsingFallback() ? this.fallbackSrc : this.src;
    }

    private isUsingFallback() {
        if (!this.fallbackSrc) return false;

        const fallbackOrErrorStatusList = [
            ImageStatus.ERROR,
            ImageStatus.FALLBACK_LOADING,
            ImageStatus.FALLBACK_LOADED
        ];

        return fallbackOrErrorStatusList.includes(this._status);
    }

    private isLoading() {
        if (this.skeletonLoading) return true;

        const loadingStatusList = [ImageStatus.LOADING, ImageStatus.FALLBACK_LOADING];

        return loadingStatusList.includes(this._status);
    }

    private onLoad() {
        if (this._status === ImageStatus.FALLBACK_LOADING) {
            this._status = ImageStatus.FALLBACK_LOADED;
            return;
        }

        this._status = ImageStatus.LOADED;
    }

    private onLoadError() {
        if (this._status === ImageStatus.ERROR) return;

        if (this._status === ImageStatus.LOADING && !!this.fallbackSrc && this.fallbackSrc !== this.src) {
            this._status = ImageStatus.FALLBACK_LOADING;
            return;
        }

        this._status = ImageStatus.ERROR;
    }

    private renderImage() {
        return html`
            <img
                class="image"
                src=${ifDefined(this.getSource() || undefined)}
                width=${ifDefined(this.width)}
                height=${ifDefined(this.height)}
                alt=${this.alt || "basic image"}
                @load=${this.onLoad}
                @error=${this.onLoadError}
            />
        `;
    }

    private renderImageSkeleton() {
        const imageSkeletonClass = {
            "image-skeleton": true,
            "show": this.isLoading()
        };

        return html` <div class=${classMap(imageSkeletonClass)}></div> `;
    }

    /** @internal */
    public render() {
        const imageWrapperClass = {
            "image-wrapper": true,
            "is-loading": this.isLoading()
        };

        const imageWrapperStyles = {
            "border-radius": this.borderRadius
        };

        return html`
            <div class=${classMap(imageWrapperClass)} style=${styleMap(imageWrapperStyles)}>
                ${this.renderImage()} ${this.renderImageSkeleton()}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-image": AtlasImage;
    }
}
